import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Heading,
	If,
	Modal,
	ModalProps,
	Paragraph,
	Row,
	Rule,
	StyleProps,
} from "@hex-insights/core";
import { projectName } from "../../../../../settings";
import { ModelFormat } from "../../../../Format";
import generalStyles from "../styles.module.css";
import styles from "./styles.module.css";

const mealPlanPriceRange = (() => {
	if (projectName === "verita-romania") {
		return { min: 1040_00, max: 1450_00 };
	}
	if (projectName === "verita-greece") {
		return { min: 0, max: 0 };
	}
	return { min: 0, max: 0 };
})();

export type ReEnrollmentMealPlanContentProps = { isWide: boolean } & Partial<
	ClassNameProps & StyleProps & ChildrenProps
>;

export function ReEnrollmentMealPlanContent({ isWide, className, style, children }: ReEnrollmentMealPlanContentProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (isWide) {
		return (
			<Row justify="space-between" className={className} style={{ rowGap: "1rem", ...style }}>
				<Column justify="spaced-center">
					<strong>
						Do you plan on purchasing a meal plan next year?<span className={generalStyles["notice__asterisk"]}>*</span>
					</strong>
					<If condition={projectName === "verita-romania"}>
						<Row justify="spaced-start" align="center">
							<span style={{ color: "var(--neutral-colors--secondary)", fontSize: "0.875rem" }}>
								Meal plan prices will range from {ModelFormat.money(mealPlanPriceRange.min)} to{" "}
								{ModelFormat.money(mealPlanPriceRange.max)}.
							</span>

							<Button variant="link" size="small" onClick={openModal}>
								View Details
							</Button>

							<Modal.If condition={isModalOpen}>
								<PricesModal onClose={closeModal} />
							</Modal.If>
						</Row>
					</If>
				</Column>

				{children}
			</Row>
		);
	}

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="1rem"
			className={generalStyles["full-tile"]}
			style={{ padding: "1rem" }}
		>
			<span>
				Do you plan on purchasing a meal plan next year?<span className={generalStyles["notice__asterisk"]}>*</span>
			</span>

			<Rule />

			<Row justify="space-between" align="center">
				<strong>Meal Plan</strong>
				{children}
			</Row>
		</Column>
	);
}

type PricesModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function PricesModal({ ifRef, onClose, className, style }: PricesModalProps) {
	return (
		<Modal size="small" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Meal Plan Prices</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={projectName === "verita-romania"}>
						<Column justify="spaced-start" verticalSpacing="1rem">
							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<strong>Early Years — Short Programme:</strong>
								<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["pricing-table"]}>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>Regular</span>
										<span>{ModelFormat.money(1090_00)}/year</span>
									</Row>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>BIO</span>
										<span>{ModelFormat.money(1250_00)}/year</span>
									</Row>
								</Column>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<strong>Early Years — Long Programme:</strong>
								<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["pricing-table"]}>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>Regular</span>
										<span>{ModelFormat.money(1290_00)}/year</span>
									</Row>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>BIO</span>
										<span>{ModelFormat.money(1450_00)}/year</span>
									</Row>
								</Column>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<strong>Primary School — Lunch only:</strong>
								<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["pricing-table"]}>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>Regular</span>
										<span>{ModelFormat.money(1040_00)}/year</span>
									</Row>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>BIO</span>
										<span>{ModelFormat.money(1200_00)}/year</span>
									</Row>
								</Column>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<strong>Primary School — Full Food:</strong>
								<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["pricing-table"]}>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>Regular</span>
										<span>{ModelFormat.money(1290_00)}/year</span>
									</Row>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>BIO</span>
										<span>{ModelFormat.money(1450_00)}/year</span>
									</Row>
								</Column>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<strong>Secondary School — Full Food:</strong>
								<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["pricing-table"]}>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>Regular</span>
										<span>{ModelFormat.money(1290_00)}/year</span>
									</Row>
									<Row justify="space-between" className={styles["pricing-table__row"]}>
										<span>BIO</span>
										<span>{ModelFormat.money(1450_00)}/year</span>
									</Row>
								</Column>
							</Column>

							<Paragraph className={styles["pricing-table__disclaimer"]}>
								*Note: For the 2024/2025 academic year, if inflation or cost of food goes up more than 10%, Verita
								reserves the right to amend the food pricing per semester (August, February).
							</Paragraph>
						</Column>
					</If>
					<If condition={projectName === "verita-greece"}>No meal plan information available.</If>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
